import type { Id } from '@shared/kernel';
import type { Money } from '@shared/lib';

export type GroupId = Id<'Group'>;

/** Group type drives the avatar icon (matches the backend `ALLOWED_GROUP_TYPES`). */
export type GroupType = 'trip' | 'home' | 'friends' | 'office' | 'roommates' | 'others';

/** A person-to-person settlement (from `settle_data`, USER↔USER only). Amount is minor units. */
export interface GroupSettlement {
  readonly fromId: string;
  readonly fromName: string;
  readonly toId: string;
  readonly toName: string;
  readonly amount: Money;
}

/** A wallet-involving settlement (from `settle_data`) — drives the user's wallet "share". */
export interface WalletSettlement {
  readonly fromId: string;
  readonly toId: string;
  readonly amount: Money;
}

/** A group member — name + avatar (+ phone) for the detail strip and the split modal. */
export interface GroupMember {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
  readonly phone: string;
  /** Email, when the API reports one for this member — the contact column's fallback when the
   *  member has no phone (mobile parity: phone first, then email, else no contact shown). */
  readonly email?: string;
  /** Backend membership role (e.g. "Owner"); null when the API doesn't report one for this row. */
  readonly role: string | null;
  /** Whether the user is a guest user (backend `is_guest`). */
  readonly isGuest?: boolean;
  /** Whether the user is a pro subscriber (backend `is_pro`). */
  readonly isPro?: boolean;
}

export interface Group {
  readonly id: GroupId;
  readonly name: string;
  readonly type: GroupType;
  readonly memberCount: number;
  readonly currency: string;
  /** Currency symbol from the API (e.g. "₹", "$", "€"). */
  readonly currencySymbol: string;
  /**
   * The current user's preferred ENTRY currency for this group (backend `user_preferred_currency_id`),
   * or '' when none — used as the default bill currency when adding an expense to the group.
   */
  readonly preferredCurrencyId: string;
  /** Your net balance in minor units: positive = you are owed, negative = you owe. */
  readonly yourBalance: Money;
  /** Status flags — drive the Active / Archived / Closed tabs. */
  readonly isArchived: boolean;
  readonly isClosed: boolean;
  /** Group-detail balance breakdown (minor units). */
  readonly sharedAmount: Money;
  readonly privateAmount: Money;
  /** Group wallet balance (minor units); null when the group has no wallet. */
  readonly walletBalance: Money | null;
  /** Whether the wallet is closed. */
  readonly isWalletClosed?: boolean;
  /** Group wallet ACCOUNT id — the `paid_by` id when the wallet pays (distinct from any member's
   *  user id); null when the group has no wallet. */
  readonly walletId: string | null;
  /** The wallet's book-keeper user ids — used to detect whether the signed-in user manages the wallet
   *  (drives the Paid-by "not book-keeper" hint); empty when unknown / no wallet. */
  readonly walletBookkeeperIds: readonly string[];
  /** Person-to-person settlements for this group (USER↔USER), names resolved from members. */
  readonly settlements: readonly GroupSettlement[];
  /** Wallet-involving settlements — used to compute the user's wallet share. */
  readonly walletSettlements: readonly WalletSettlement[];
  /** Group members (name + avatar) for the detail header. */
  readonly members: readonly GroupMember[];
  /** False when this group has never had a transaction (backend `last_transaction_at` is null) —
   *  drives the My Groups rail's "Waiting for the first expense" empty state for a self group. */
  readonly hasExpenses: boolean;
  /** The group creator's user id (members dialog "Creator" tag); null when the API omits it. */
  readonly createdById: string | null;
  readonly startDate?: Date | null;
  readonly endDate?: Date | null;
  readonly createdAt: Date | null;
  readonly closedAt: Date | null;
  readonly lastTransactionAt: Date | null;
  readonly updatedAt: Date;
}

/** A group is "active" when it is neither archived nor closed (matches the backend `ongoing` state). */
export function isActiveGroup(group: Group): boolean {
  return !group.isArchived && !group.isClosed;
}

/** Backend role spellings that mean a read-only membership (compared trimmed + lowercased). */
const VIEW_ONLY_ROLES = new Set(['viewer', 'view only']);

/**
 * Whether `userId`'s membership in `group` is read-only (backend role "Viewer" / "View Only").
 * Such a member cannot add or change expenses — drives the add-expense permission guards.
 */
export function isViewOnlyMember(group: Group, userId: string): boolean {
  const member = group.members.find((m) => m.id === userId);
  return member?.role != null && VIEW_ONLY_ROLES.has(member.role.trim().toLowerCase());
}
